import { useMemo } from 'react'
import type { InitiativeListItem } from '@/domain/types'
import { useInitiatives } from './useInitiatives'
import type { QueryState } from '@/api/useApiQuery'

/** Widest page the API allows (`pageSize` max is 100 per api-contracts.md). */
const WINDOW = 100

/**
 * The most recently *modified* initiatives, for the home "Recently updated" list.
 *
 * `GET /api/initiatives` orders by `InitiativeId DESC` and takes no sort
 * param, so newest-created is the only server ordering available — which is
 * not the same thing as recently-touched. We therefore pull the widest page
 * the contract allows and order by `modifiedAt` here.
 *
 * The honest limit: past 100 initiatives, an old initiative edited this
 * morning can fall outside the window and miss the list. At that point the
 * right fix is a `sort=modifiedAt` param on the endpoint, not a bigger
 * client-side window.
 */
export function useRecentInitiatives(limit: number): {
  query: QueryState<unknown>
  initiatives: InitiativeListItem[]
} {
  const query = useInitiatives({ page: 1, pageSize: WINDOW })

  const initiatives = useMemo(() => {
    if (query.status !== 'success') return []
    return sortByRecency(query.data.data).slice(0, limit)
  }, [query, limit])

  return { query, initiatives }
}

/**
 * Newest `modifiedAt` first. Ties (and unparseable timestamps, which sort
 * last) fall back to `initiativeId` descending so the order is stable
 * across renders.
 */
export function sortByRecency(rows: readonly InitiativeListItem[]): InitiativeListItem[] {
  return [...rows].sort((a, b) => {
    const ta = timeOf(a.modifiedAt)
    const tb = timeOf(b.modifiedAt)
    if (ta !== tb) return tb - ta
    return b.initiativeId - a.initiativeId
  })
}

function timeOf(iso: string): number {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? -Infinity : t
}
